// MARK://dhk8/pos/hooks/use-lockscreen
// Terminal-lock triggers:
//   • idle-timeout — after IDLE_LOCK_MS of no pointer/key/touch input the terminal locks
//     itself (default 5 min). The timer resets on any input and is disarmed while the
//     terminal is already locked or there's no authenticated user.
//   • double-Esc — two Escape presses within DOUBLE_ESC_WINDOW_MS lock immediately
//     (kept from the original port of sc-restaurant's useDoubleEscLock).
// A manual lock button lives in the topbar (PosLayout) and just calls lock-store.lock().
// Attaches to `window`, so it works on every POS surface regardless of which page mounted it.

import { useEffect, useRef } from 'react'
import { useAuthStore } from '../stores/auth-store.js'
import { useLockStore } from '../stores/lock-store.js'

const DOUBLE_ESC_WINDOW_MS = 800
/** Idle period after which the terminal auto-locks (5 minutes). */
export const IDLE_LOCK_MS = 5 * 60_000

export function useLockscreen() {
  const hasUser = useAuthStore((s) => !!s.user)
  const locked = useLockStore((s) => s.locked)
  const lock = useLockStore((s) => s.lock)
  const lastEscRef = useRef(0)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Idle-timeout auto-lock ────────────────────────────────────────────
  useEffect(() => {
    if (!hasUser || locked) {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
      return
    }
    const reset = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => lock(), IDLE_LOCK_MS)
    }
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel']
    for (const ev of events) window.addEventListener(ev, reset, { passive: true })
    reset()
    return () => {
      for (const ev of events) window.removeEventListener(ev, reset)
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
    }
  }, [hasUser, locked, lock])

  // ── Double-Esc lock ───────────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (!hasUser || locked) return
      const now = Date.now()
      if (now - lastEscRef.current <= DOUBLE_ESC_WINDOW_MS) {
        e.preventDefault()
        lastEscRef.current = 0
        lock()
      } else {
        lastEscRef.current = now
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [hasUser, locked, lock])
}
